import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedDrilldownInputPassthroughMap } from '../Shared/ManagedDrilldown';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    SlimDataSetInputAdminModel,
    mapSlimDataSetInputAdminModel,
} from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import GetDataSetInputAdminModelsRequest from '../../Interfaces/Requests/Administration/GetDataSetInputAdminModelsRequest';
import GetDataSetInputAdminModelResponse from '../../Interfaces/Responses/Administration/GetDataSetInputAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';

export type ManagedDrilldownInputPassthroughMapEditResult = {
    operation: ManagedAdminChildOperation;
    drilldownInputPassthroughMap: ManagedDrilldownInputPassthroughMap;
};

type AUDDrilldownPassthroughMapFormValues = {
    ddipmSourceDataSetInputID: number;
    ddipmTargetDataSetInputID: number;
};

type AUDDrilldownPassthroughMapsProps = {
    drilldownID: number;
    drilldownTitle: string;
    sourceDataSetID: number;
    sourceDataSetName: string;
    targetDataSetID: number;
    targetDataSetName: string;
    drilldownInputPassthroughMaps: ManagedDrilldownInputPassthroughMap[];
    initialSelectedDrilldownInputPassthroughMapID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetDrilldownInputPassthroughMaps: (
        result: ManagedDrilldownInputPassthroughMapEditResult
    ) => void;
    close: () => void;
};

const AUDDrilldownPassthroughMaps: React.FC<AUDDrilldownPassthroughMapsProps> = ({ close, ...props }) => {
    const [formAUDDrilldownPassthroughMap] = Form.useForm();

    const passthroughMaps = props.drilldownInputPassthroughMaps ?? [];

    const getInitialDDIPMMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedPassthroughMap = (): ManagedDrilldownInputPassthroughMap | undefined => {
        if (!props.initialSelectedDrilldownInputPassthroughMapID || props.initialSelectedDrilldownInputPassthroughMapID <= 0) {
            return undefined;
        }

        return passthroughMaps.find(
            (passthroughMap) => passthroughMap.id === props.initialSelectedDrilldownInputPassthroughMapID
        );
    };

    const buildPassthroughMapNameConstruct = (passthroughMap: ManagedDrilldownInputPassthroughMap | undefined): string => {
        if (!passthroughMap) {
            return '';
        }

        return 'Source Input: [' + passthroughMap.sourceDataSetInputId + '] -> Target Input: [' + passthroughMap.targetDataSetInputId + ']';
    };

    const initialSelectedPassthroughMap = getInitialSelectedPassthroughMap();
    const initialDDIPMMode = getInitialDDIPMMode();

    const initialDDIPMFormValues = initialDDIPMMode === 'add'
        ? {
            ddipmSourceDataSetInputID: 0,
            ddipmTargetDataSetInputID: 0,
        }
        : {
            ddipmSourceDataSetInputID: initialSelectedPassthroughMap?.sourceDataSetInputId ?? 0,
            ddipmTargetDataSetInputID: initialSelectedPassthroughMap?.targetDataSetInputId ?? 0,
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialDDIPMMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialDDIPMMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialDDIPMMode === 'delete');

    const [ddipmSelectedPassthroughMapID, setDDIPMSelectedPassthroughMapID] = useState(initialSelectedPassthroughMap?.id ?? -1);

    const [ddipmSourceDataSetInputID, setDDIPMSourceDataSetInputID] = useState<number>(initialSelectedPassthroughMap?.sourceDataSetInputId ?? 0);
    const [ddipmTargetDataSetInputID, setDDIPMTargetDataSetInputID] = useState<number>(initialSelectedPassthroughMap?.targetDataSetInputId ?? 0);

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const includeDisabled = false;

    const formatDDIPMDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const getDDIPMOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getDDIPMActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const setDDIPMMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const validateDDIPMDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'ddipmSourceDataSetInputID':
                return (formAUDDrilldownPassthroughMap.getFieldValue('ddipmSourceDataSetInputID') ?? 0) > 0
                    ? Promise.resolve()
                    : Promise.reject();

            case 'ddipmTargetDataSetInputID':
                return (formAUDDrilldownPassthroughMap.getFieldValue('ddipmTargetDataSetInputID') ?? 0) > 0
                    ? Promise.resolve()
                    : Promise.reject();

            default:
                return Promise.reject();
        }
    };

    const handleDDIPMLoadError = (title: string, err: unknown) => {
        const apiError = err as ManagedApiError;

        const message = apiError?.response?.data?.message
            ? `${title}: ${apiError.response.data.message}`
            : `${title}: ${String(err)}`;

        setManagedActionModalState(
            buildManagedActionModalState('info', 'Passthrough Map Load Error', message, true)
        );
    };

    const { data: sourceDataSetInputs = [], isLoading: isGetSourceDataSetInputsLoading } = useQuery<GetDataSetInputAdminModelResponse[], Error, SlimDataSetInputAdminModel[]>(
        ['getSourceDataSetInputAdminModelsRequest', props.sourceDataSetID, includeDisabled],
        async (): Promise<GetDataSetInputAdminModelResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_INPUT_ADMIN_MODELS}`;
                const requestData: GetDataSetInputAdminModelsRequest = {
                    gdsi_datasetID: props.sourceDataSetID,
                    gdsi_includeDisabled: includeDisabled,
                };

                return await AxiosUtil.get<GetDataSetInputAdminModelResponse[], GetDataSetInputAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleDDIPMLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get Source DataSet Input Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.sourceDataSetID > 0,
            onError: (err: Error) => {
                handleDDIPMLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get Source DataSet Input Admin Models] Internal Error', err);
            },
            select: (response: GetDataSetInputAdminModelResponse[]): SlimDataSetInputAdminModel[] => {
                return response
                    .map(mapSlimDataSetInputAdminModel)
                    .sort((a, b) => a.title.localeCompare(b.title));
            },
        }
    );

    const { data: targetDataSetInputs = [], isLoading: isGetTargetDataSetInputsLoading } = useQuery<GetDataSetInputAdminModelResponse[], Error, SlimDataSetInputAdminModel[]>(
        ['getTargetDataSetInputAdminModelsRequest', props.targetDataSetID, includeDisabled],
        async (): Promise<GetDataSetInputAdminModelResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_INPUT_ADMIN_MODELS}`;
                const requestData: GetDataSetInputAdminModelsRequest = {
                    gdsi_datasetID: props.targetDataSetID,
                    gdsi_includeDisabled: includeDisabled,
                };

                return await AxiosUtil.get<GetDataSetInputAdminModelResponse[], GetDataSetInputAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleDDIPMLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get Target DataSet Input Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.targetDataSetID > 0,
            onError: (err: Error) => {
                handleDDIPMLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get Target DataSet Input Admin Models] Internal Error', err);
            },
            select: (response: GetDataSetInputAdminModelResponse[]): SlimDataSetInputAdminModel[] => {
                return response
                    .map(mapSlimDataSetInputAdminModel)
                    .sort((a, b) => a.title.localeCompare(b.title));
            },
        }
    );

    const setDDIPMBlankForm = () => {
        formAUDDrilldownPassthroughMap.setFieldValue('ddipmSourceDataSetInputID', undefined);
        formAUDDrilldownPassthroughMap.setFieldValue('ddipmTargetDataSetInputID', undefined);

        setDDIPMSourceDataSetInputID(0);
        setDDIPMTargetDataSetInputID(0);
    };

    const setDDIPMFormFromSelectedPassthroughMap = () => {
        const selectedPassthroughMap = passthroughMaps.find(
            (passthroughMap) => passthroughMap.id === ddipmSelectedPassthroughMapID
        );

        formAUDDrilldownPassthroughMap.setFieldValue('ddipmSourceDataSetInputID', selectedPassthroughMap?.sourceDataSetInputId ?? undefined);
        formAUDDrilldownPassthroughMap.setFieldValue('ddipmTargetDataSetInputID', selectedPassthroughMap?.targetDataSetInputId ?? undefined);

        setDDIPMSourceDataSetInputID(selectedPassthroughMap?.sourceDataSetInputId ?? 0);
        setDDIPMTargetDataSetInputID(selectedPassthroughMap?.targetDataSetInputId ?? 0);
    };

    const handleDDIPMPassthroughMapResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setDDIPMBlankForm();
            return;
        }

        setDDIPMFormFromSelectedPassthroughMap();
    };

    const clearDDIPMSelection = () => {
        setDDIPMSelectedPassthroughMapID(-1);
        setDDIPMMode('add');
        handleDDIPMPassthroughMapResetForm('blank');
    };

    const handleDDIPMSelectionChange = (value: number) => {
        setDDIPMSelectedPassthroughMapID(value);
        setDDIPMMode('add');
        handleDDIPMPassthroughMapResetForm('blank');
    };

    const buildManagedPassthroughMapRequest = (values: AUDDrilldownPassthroughMapFormValues): ManagedDrilldownInputPassthroughMap => {
        return {
            id: addButtonClicked ? 0 : ddipmSelectedPassthroughMapID,
            drilldownId: props.drilldownID,
            sourceDataSetInputId: values.ddipmSourceDataSetInputID ?? 0,
            targetDataSetInputId: values.ddipmTargetDataSetInputID ?? 0,
            deleted: delButtonClicked,
        };
    };

    const onFinish = (values: AUDDrilldownPassthroughMapFormValues) => {
        const newManagedPassthroughMap = buildManagedPassthroughMapRequest(values);

        props.handleSetDrilldownInputPassthroughMaps({
            operation: getDDIPMOperation(),
            drilldownInputPassthroughMap: newManagedPassthroughMap,
        });

        close();
    };

    const onFinishFailed = (_errorInfo: ManagedFormErrorInfo<AUDDrilldownPassthroughMapFormValues>) => {
        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Passthrough Map Validation',
                'Please correct the highlighted Passthrough Map fields and try again.',
                true
            )
        );
    };

    const handleDDIPMAddPassthroughMap = () => {
        clearDDIPMSelection();
    };

    const handleDDIPMUpdPassthroughMap = () => {
        setDDIPMMode('update');
        handleDDIPMPassthroughMapResetForm('selected');
    };

    const handleDDIPMDelPassthroughMap = () => {
        setDDIPMMode('delete');
        handleDDIPMPassthroughMapResetForm('selected');
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: '1px solid black',
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <Spin spinning={isGetSourceDataSetInputsLoading || isGetTargetDataSetInputsLoading}>
                <Space direction='horizontal' style={{ justifyContent: 'center', padding: '4px', width: '100%' }}>
                    <Text style={{ height: '100%', width: '100%' }}>
                        <Title style={{ backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%' }} level={4}>
                            Manage Drilldown Input Passthrough Maps
                        </Title>
                    </Text>
                </Space>

                <Space direction='horizontal' style={{ justifyContent: 'center', padding: '2px', width: '100%' }}>
                    <Text italic type='secondary'>{'Drilldown: [' + formatDDIPMDisplayID(props.drilldownID) + '] ' + props.drilldownTitle}</Text>
                </Space>
                <Space direction='horizontal' style={{ justifyContent: 'center', padding: '2px', width: '100%' }}>
                    <Text italic type='secondary'>{'Source DataSet: [' + props.sourceDataSetID + '] ' + props.sourceDataSetName}</Text>
                </Space>
                <Space direction='horizontal' style={{ justifyContent: 'center', padding: '2px', width: '100%' }}>
                    <Text italic type='secondary'>{'Target DataSet: [' + props.targetDataSetID + '] ' + props.targetDataSetName}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Input Passthrough Map to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder='Select a Passthrough Map'
                            showSearch
                            style={{ paddingTop: '4px', width: '95%' }}
                            onChange={handleDDIPMSelectionChange}
                        >
                            {passthroughMaps.map((passthroughMap) => {
                                return (
                                    <Option
                                        key={passthroughMap.id}
                                        label={buildPassthroughMapNameConstruct(passthroughMap)}
                                        value={passthroughMap.id}
                                    >
                                        {buildPassthroughMapNameConstruct(passthroughMap)}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={6}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDDIPMAddPassthroughMap}
                        >
                            Add Input Passthrough Map
                        </Button>
                    </Col>
                    <Col span={6} />
                    <Col span={6}>
                        <Button
                            disabled={ddipmSelectedPassthroughMapID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDDIPMUpdPassthroughMap}
                        >
                            Update Input Passthrough Map
                        </Button>
                    </Col>
                    <Col span={6}>
                        <Button
                            disabled={ddipmSelectedPassthroughMapID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDDIPMDelPassthroughMap}
                        >
                            Delete Input Passthrough Map
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDDrilldownPassthroughMap}
                    initialValues={initialDDIPMFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getDDIPMActionColor() }}>
                                    {addButtonClicked ? 'Add Passthrough Map' :
                                        (updButtonClicked ? 'Update Passthrough Map: [' + formatDDIPMDisplayID(ddipmSelectedPassthroughMapID) + '] ' :
                                            (delButtonClicked ? 'Delete Passthrough Map: [' + formatDDIPMDisplayID(ddipmSelectedPassthroughMapID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getDDIPMActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleDDIPMPassthroughMapResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <Form.Item
                                    name='ddipmSourceDataSetInputID'
                                    rules={[{ validator: validateDDIPMDetailsData }]}
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Select
                                        disabled={delButtonClicked}
                                        styles={{ popup: { root: { width: 500 } } }}
                                        filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                        label='Source Input:'
                                        placeholder='Select Source Input'
                                        showSearch
                                        value={ddipmSourceDataSetInputID > 0 ? ddipmSourceDataSetInputID : undefined}
                                        onChange={(value: number) => setDDIPMSourceDataSetInputID(value ?? 0)}
                                    >
                                        {sourceDataSetInputs.map((dataSetInput) => {
                                            return (
                                                <Option
                                                    key={dataSetInput.id}
                                                    label={'[' + dataSetInput.id + '] ' + dataSetInput.title}
                                                    value={dataSetInput.id}
                                                >
                                                    {'[' + dataSetInput.id + '] ' + dataSetInput.title}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name='ddipmTargetDataSetInputID'
                                    rules={[{ validator: validateDDIPMDetailsData }]}
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Select
                                        disabled={delButtonClicked}
                                        styles={{ popup: { root: { width: 500 } } }}
                                        filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                        label='Target Input:'
                                        placeholder='Select Target Input'
                                        showSearch
                                        value={ddipmTargetDataSetInputID > 0 ? ddipmTargetDataSetInputID : undefined}
                                        onChange={(value: number) => setDDIPMTargetDataSetInputID(value ?? 0)}
                                    >
                                        {targetDataSetInputs.map((dataSetInput) => {
                                            return (
                                                <Option
                                                    key={dataSetInput.id}
                                                    label={'[' + dataSetInput.id + '] ' + dataSetInput.title}
                                                    value={dataSetInput.id}
                                                >
                                                    {'[' + dataSetInput.id + '] ' + dataSetInput.title}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={6}>
                            <Form.Item
                                name='ddipmCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6} />
                        <Col span={6}>
                            <Form.Item
                                name='ddipmConfirmButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: getDDIPMActionColor(), width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Input Passthrough Map' : (updButtonClicked ? 'Update Passthrough Map' : (delButtonClicked ? 'Delete Passthrough Map' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name='ddipmCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={() => handleDDIPMPassthroughMapResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDDrilldownPassthroughMaps;
